import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { categories, articles } from '@/mocks/siteData';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  hot: {
    title: '热门推荐 - 禁漫天堂',
    description: '禁漫天堂热门推荐专区，每日精选全网最受欢迎的成人漫画。涵盖日漫、韩漫、国漫、3D漫画等多类型热门作品，热榜排行实时更新。',
    keywords: '禁漫天堂热门推荐,成人漫画排行,热门漫画,漫画推荐',
  },
  new: {
    title: '最新上架 - 禁漫天堂',
    description: '禁漫天堂最新上架专区，第一时间获取最新发行的成人漫画作品。每日更新，追更不等待，热门新作抢先看。',
    keywords: '禁漫天堂最新上架,新漫画,最新成人漫画,漫画更新',
  },
  japanese: {
    title: '日漫专区 - 禁漫天堂',
    description: '禁漫天堂日漫专区，精选日本成人漫画。涵盖恋爱、奇幻、异世界、校园等多元题材，海量优质日漫作品持续更新中。',
    keywords: '禁漫天堂日漫,日本成人漫画,日漫推荐,日漫在线观看',
  },
  korean: {
    title: '韩漫专区 - 禁漫天堂',
    description: '禁漫天堂韩漫专区，汇集韩国热门成人漫画。画风精致细腻，剧情引人入胜，竖版滚动阅读，沉浸式体验。',
    keywords: '禁漫天堂韩漫,韩国成人漫画,韩漫推荐,韩漫在线观看',
  },
  chinese: {
    title: '国漫专区 - 禁漫天堂',
    description: '禁漫天堂国漫专区，支持国产原创成人漫画。汇集本土优秀创作者作品，东方美学与现代漫画技法完美融合。',
    keywords: '禁漫天堂国漫,国产成人漫画,国漫推荐,国产原创漫画',
  },
  '3d': {
    title: '3D漫画 - 禁漫天堂',
    description: '禁漫天堂3D漫画专区，高品质3D渲染成人漫画合集。逼真立体画面，沉浸式视觉盛宴，重新定义漫画阅读体验。',
    keywords: '禁漫天堂3D漫画,3D成人漫画,3D渲染漫画,3D漫画推荐',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'hot');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const currentCategory = useMemo(() => categories.find((c) => c.slug === activeSlug), [activeSlug]);
  const meta = categoryMeta[activeSlug] || categoryMeta.hot;
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  if (!currentCategory) {
    return (
      <div className="pt-24 text-center">
        <p className="text-foreground-600">分类未找到</p>
        <Link to="/" className="text-primary-600 hover:underline mt-2 inline-block">返回首页</Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalPath={`/category/${activeSlug}`}
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: currentCategory.name,
            description: currentCategory.description,
            url: `https://jmtthub.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '禁漫天堂',
              url: 'https://jmtthub.com',
            },
          },
        ]}
      />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumbs
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/hot' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        <section className="w-full py-8 md:py-12 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mb-8">
              {currentCategory.description}
            </p>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-700 hover:bg-background-300'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/30"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.readTime}阅读</span>
                      </div>
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}